/* CreateFileDetailsRenderer.js */

"use strict";
const ButtonControl = require("@gluenetworks/steve_ui_core").Control.Action.Button;
const InputTextControl = require("@gluenetworks/steve_ui_core").Control.Textfield.InputText;
const ComboBoxControl = require("@gluenetworks/steve_ui_core").Control.OptionSelection.Single
    .ComboBox;
// const { VENDOR_LIST_FILTER, IMAGE_TYPES_FILTER } = require("../../fileConstants");
const _ = require("lodash");

class CreateFileDetailsRenderer {
    constructor(model, initialData = {}) {
        this._model = model;
        this._initialData = initialData;
        this._rootElem = document.createElement("create-file-gl");
        this._rootElem.className = "modal_feedback_container";

        this._rootElem.component = this._model;

        this._isMultiple = this.model.osImages.length > 1;
        let name = this.model.osImages.map((osImage) => osImage.name).join(", ");

        this._nameField = new InputTextControl({
            darkTheme: true,
            placeholder: "Enter a file name",
            tooltip: name,
            disabled: this._isMultiple,
            value: name,
        });

        this._vendorList = undefined;
        this._imageTypeList = undefined;
        this._initialData = initialData;

        this._catalogEntryList = new ComboBoxControl({
            placeholder: "Select a catalog entry (optional)",
            options: [],
            allowClear: true,
            visible: false,
        });

        this._cancelButton = new ButtonControl({
            label: "Cancel",
            darkTheme: true,
        });

        this._confirmButton = new ButtonControl({
            label: "Save",
            darkTheme: true,
            theme: "additive",
        });

        this._titleComponent = document.createElement("h2");
        this._titleComponent.innerText = "Add Files";

        this._formContainer = document.createElement("div");
        this._formContainer.className = "form-container";

        this._buttonContainer = document.createElement("div");
        this._buttonContainer.className = "actions_container";

        this._registerListeners();
    }

    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/
    _registerListeners() {
        this._confirmButton.on(
            "UserClick",
            (this._onConfirmButtonClick = this._onConfirmButtonClick.bind(this))
        );
        this._cancelButton.on(
            "UserClick",
            (this._onCancelButtonClick = this._onCancelButtonClick.bind(this))
        );
        this._nameField.on(
            "UserKeyDown",
            (this._checkForImpliedConfirm = this._checkForImpliedConfirm.bind(this))
        );
        this._nameField.on("UserValue", (this.checkValue = this.checkValue.bind(this)));
    }

    _unregisterListeners() {
        this._confirmButton.removeAllListeners("UserClick");
        this._cancelButton.removeAllListeners("UserClick");
        this._nameField.removeAllListeners("UserKeyDown");
        this._nameField.removeAllListeners("UserValue");
    }

    _onConfirmButtonClick() {
        this._submitForm();
    }

    _onCancelButtonClick() {
        let err = new Error("user rejected");
        err.userRejected = true;
        this.model.cancelAction(err);
    }

    _checkForImpliedConfirm(event) {
        if (event.keyCode === 13) {
            this._submitForm();
        }
    }

    checkValue(value) {
        this._confirmButton.disabled = _.isEmpty(value) || value.trim().length === 0;
    }

    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/
    shutdown() {
        this._unregisterListeners();
        let promArr = [
            this._nameField.shutdown(),
            this._vendorList.shutdown(),
            this._imageTypeList.shutdown(),
            this._catalogEntryList.shutdown(),
            this._confirmButton.shutdown(),
            this._cancelButton.shutdown(),
        ];

        return Promise.all(promArr).then(
            () => {
                this._nameField = undefined;
                this._confirmButton = undefined;
                this._buttonContainer.remove();
                this._buttonContainer = undefined;
                this._titleComponent.remove();
                this._titleComponent = undefined;
                this._model = undefined;
                this._rootElem.remove();
                this._rootElem = undefined;
                this._initialData = undefined;
            },
            (err) => {
                this._model.logger.warn(err, "CreateQuestionRenderer.shutdown() error");
            }
        );
    }

    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
     * Promise is returned the caller will assume the Renderer has completed rendering.
     */
    render() {
        return this._model.whenModelIsReady().then(({imageCatalogs}) => {
            const initialCatalogValue = this._initialData.imageCatalog;
            const catalogList = _.map(imageCatalogs, (imageCatalog) => ({
                label: imageCatalog.name,
                value: imageCatalog.id,
            }));
            const showCatalogs = Boolean(catalogList.length);

            this._vendorList = new ComboBoxControl({
                placeholder: "Select a vendor (optional)",
                options: Object.values(this._model._vendorListFilter).map(({name, title}) => ({
                    label: title,
                    value: name,
                })),
                allowClear: true,
            });
            if (
                this._initialData.vendor &&
                this._model._vendorListFilter[this._initialData.vendor]
            ) {
                this._vendorList.setValue(this._initialData.vendor);
            }

            this._imageTypeList = new ComboBoxControl({
                placeholder: "Select an image type (optional)",
                options: Object.values(this._model._imageTypeFilter).map(({name, title}) => ({
                    label: title,
                    value: name,
                })),
                allowClear: true,
            });
            if (
                this._initialData.imageType &&
                this._model._imageTypeFilter[this._initialData.imageType]
            ) {
                this._imageTypeList.setValue(this._initialData.imageType);
            }

            this._catalogEntryList.setOptions(catalogList);
            this._catalogEntryList.setVisible(showCatalogs);

            if (_.some(catalogList, {value: initialCatalogValue})) {
                this._catalogEntryList.setValue(initialCatalogValue);
            }

            if (!this._rootElem.hasChildNodes()) {
                const root = document.createDocumentFragment();

                this._formContainer.appendChild(
                    this._inputContainer(this._nameField, this._isMultiple ? "Names" : "Name")
                );
                this._formContainer.appendChild(this._inputContainer(this._vendorList, "Vendor"));
                this._formContainer.appendChild(
                    this._inputContainer(this._imageTypeList, "Image Type")
                );
                if (showCatalogs) {
                    this._formContainer.appendChild(
                        this._inputContainer(this._catalogEntryList, "Catalog Entry")
                    );
                }
                this._buttonContainer.appendChild(this._cancelButton.renderer.rootElement);
                this._buttonContainer.appendChild(this._confirmButton.renderer.rootElement);

                root.appendChild(this._titleComponent);
                root.appendChild(this._formContainer);
                root.appendChild(this._buttonContainer);

                this._rootElem.appendChild(root);
                setTimeout(() => {
                    this._nameField.focus();
                });
            }
        });
    }

    get rootElement() {
        return this.getRootElement();
    }

    getRootElement() {
        return this._rootElem;
    }

    get model() {
        return this.getModel();
    }

    getModel() {
        return this._model;
    }

    /*******************************************/
    /* END COMPONENT RENDERER METHODS          */
    /* START PRIVATE RENDER METHODS            */
    /*******************************************/

    _inputContainer(inputComponent, labelText) {
        const inputContainer = document.createElement("div");
        inputContainer.className = "input-container";

        const label = document.createElement("label");
        label.innerText = labelText;

        const inputControl = document.createElement("div");
        inputControl.className = "input-control";
        inputControl.appendChild(inputComponent.renderer.rootElement);

        inputContainer.appendChild(label);
        inputContainer.appendChild(inputControl);
        return inputContainer;
    }

    /*******************************************/
    /* END PRIVATE RENDER METHODS              */
    /* START HANDLER METHODS                   */
    /*******************************************/

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE API METHODS               */
    /*******************************************/
    _submitForm() {
        if (_.isEmpty(this._nameField.getValue())) {
            return;
        }
        this._model.submit({
            name: this._nameField.getValue(),
            vendor: this._vendorList.getValue(),
            imageType: this._imageTypeList.getValue(),
            catalog: this._catalogEntryList.getValue(),
        });
    }

    /*******************************************/
    /*  END PRIVATE API METHODS                */
    /*******************************************/
}

module.exports = CreateFileDetailsRenderer;
